// ============================================================================
// OverviewSection — the headline "at a glance" KPI strip.
// Reuses the locked StatTile (animated count-up). Each tile renders only when
// its underlying number exists, so a sparse report shows fewer tiles rather
// than fabricated zeros.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { StatTile } from "../../../components";
import { IconCalendar } from "../../../components/icons";
import type { TypedClientReport } from "../types";
import { fmtSigned } from "../format";

interface TileSpec {
  key: string;
  label: string;
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  delta?: string;
  deltaTone?: "up" | "down" | "flat";
  accent: "coral" | "violet" | "ocean" | "lime";
  icon?: React.ReactNode;
}

/** Build the KPI tiles present for this report (only those with data). */
function buildTiles(
  report: TypedClientReport,
  t: (k: string) => string,
  locale: string,
): TileSpec[] {
  const tiles: TileSpec[] = [];
  const { adherence, strength, body } = report;

  if (adherence.ok) {
    tiles.push({
      key: "sessions",
      label: t("overview.sessionsDone"),
      value: adherence.completed,
      accent: "coral",
      icon: <IconCalendar width={20} height={20} />,
    });
    if (adherence.completion_pct !== null) {
      tiles.push({
        key: "completion",
        label: t("overview.completion"),
        value: adherence.completion_pct,
        suffix: "%",
        accent: "ocean",
      });
    }
  }

  if (strength.ok && strength.total_tonnage !== null) {
    tiles.push({
      key: "tonnage",
      label: t("overview.tonnage"),
      value: strength.total_tonnage,
      decimals: 0,
      suffix: " kg",
      accent: "violet",
    });
  }

  const wd = body.deltas.weight;
  if (wd) {
    tiles.push({
      key: "weight",
      label: t("overview.weightChange"),
      value: Math.abs(Number(wd.change.toFixed(1))),
      decimals: 1,
      prefix: wd.change > 0 ? "+" : wd.change < 0 ? "−" : "",
      suffix: " kg",
      delta: `${fmtSigned(wd.change, 1, locale)} ${t("overview.since")}`,
      // Weight LOSS is the usual win → show green when it drops.
      deltaTone: wd.change < 0 ? "up" : wd.change > 0 ? "down" : "flat",
      accent: "lime",
    });
  }

  return tiles;
}

export function OverviewSection({ report }: { report: TypedClientReport }) {
  const { t, i18n } = useTranslation("clientReport");
  const reduce = useReducedMotion();
  const tiles = buildTiles(report, t, i18n.language);

  if (tiles.length === 0) return null;

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
    >
      {tiles.map((tile) => (
        <StatTile
          key={tile.key}
          label={tile.label}
          value={tile.value}
          decimals={tile.decimals ?? 0}
          prefix={tile.prefix}
          suffix={tile.suffix}
          delta={tile.delta}
          deltaTone={tile.deltaTone}
          accent={tile.accent}
          icon={tile.icon}
        />
      ))}
    </motion.div>
  );
}
